import {
  GET_DISPLAY_PREFERENCE,
  UPDATE_DISPLAY_PREFERENCE,
} from "@/graphql/pubmed.graphql";
import useTranslation from "@/hooks/useTranslation";
import { DisplayPreference } from "@/interfaces/pubmed.interface";
import { currentUserState } from "@/stores/common";
import { pubmedMergeFieldState } from "@/stores/pubmed";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { Select, Switch } from "antd";
import React, { useMemo } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import styles from "./styles.module.less";

const { container, flex, label, left, full } = styles;

const { Option } = Select;

const Display: React.FC = () => {
  const [
    { verbose, immersiveMode, defaultActiveKey },
    setDisplayPreference,
  ] = useRecoilState<Partial<DisplayPreference>>(
    pubmedMergeFieldState("displayPreference")
  );

  const currentUser = useRecoilValue(currentUserState);

  const { data } = useQuery<{ getPreference: { display: DisplayPreference } }>(
    GET_DISPLAY_PREFERENCE,
    { skip: !currentUser?.id }
  );

  useMemo(() => {
    if (data) {
      setDisplayPreference(data?.getPreference?.display);
    }
  }, [data]);

  const [mutatePreference] = useMutation<
    { getPreference: { display: DisplayPreference } },
    { update: Partial<DisplayPreference> }
  >(UPDATE_DISPLAY_PREFERENCE);

  const updatePreference = (p: Partial<DisplayPreference>) => {
    setDisplayPreference(p);
    if (currentUser?.id) {
      mutatePreference({ variables: { update: p } });
    }
  };

  const toggleVerbose = () => updatePreference({ verbose: !verbose });

  const toggleImmersiveMode = () =>
    updatePreference({ immersiveMode: !immersiveMode });

  const onChangeDefault = (key: string) => {
    updatePreference({ defaultActiveKey: key });
  };

  const { t } = useTranslation("explore");

  return (
    <div className={container}>
      <div className={flex}>
        <div className={label} style={{ marginBottom: 0 }}>
          {t("pubmed.sidebar.display.advanced-mode")}
        </div>
        <div className={left}>
          <Switch checked={verbose} onChange={toggleVerbose} />
        </div>
      </div>
      <div>
        <div className={label}>
          {t("pubmed.sidebar.display.default-display")}
        </div>
        <div className={left}>
          <Select
            value={defaultActiveKey}
            onChange={onChangeDefault}
            disabled={!verbose}
            className={full}
          >
            <Option value="Helper">{t("pubmed.sidebar.display.helper")}</Option>
            <Option value="Abstract">
              {t("pubmed.sidebar.display.abstract")}
            </Option>
          </Select>
        </div>
      </div>
      <div className={flex}>
        <div className={label} style={{ marginBottom: 0 }}>
          {t("pubmed.sidebar.display.immersive-mode")}
        </div>
        <div className={left}>
          <Switch
            checked={immersiveMode}
            onChange={toggleImmersiveMode}
            disabled={!verbose}
          />
        </div>
      </div>
    </div>
  );
};

export default Display;
